/**
 * Configuration constants for Zwaailicht.nu
 */

// Map tile layer (PDOK BRT Achtergrondkaart — Kadaster's keyless, no-account
// NL basemap; the "water" style keeps land near white and puts the colour in
// the water, clear of the service-dot palette). Replaces CartoDB, which now
// gates its tiles behind an API key. Single host, no {s} subdomains and no {r}
// retina variant (PDOK serves neither).
export const MAP_TILES = {
    url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/water/EPSG:3857/{z}/{x}/{y}.png',
    attribution: 'Kaartgegevens &copy; <a href="https://www.pdok.nl">Kadaster</a>',
    // BRT-A tops out at z18 in Web Mercator; Leaflet over-zooms past that.
    maxNativeZoom: 18,
};

// Map settings
export const MAP_CENTER = [52.1326, 5.2913]; // Netherlands center
export const MAP_ZOOM = 8;
export const MAP_MAX_ZOOM = 20;

// Alert limits
export const MAX_ALERTS = 100;
export const MAX_UNLOCATED_ALERTS = 50;

// Notification settings
export const NOTIFICATION_RADIUS_KM_DEFAULT = 15;

// Default radius for filtering — used for marker dimming and notification gating
export const FILTER_RADIUS_KM_DEFAULT = 50;
export const FILTER_RADIUS_KM_MIN = 5;
export const FILTER_RADIUS_KM_MAX = 150;

// Service types and their icons/colors. Colours match the v8 service tokens
// in shared.css (--brandweer/--ambulance/--politie/--lifeliner/--knrm/--overig)
// so the map markers and route lines painted from these
// hexes agree with the CSS-driven colours everywhere else. (knrm is green now.)
export const SERVICES = {
    brandweer: { icon: '🔥', color: '#e0322a' },
    ambulance: { icon: '🚑', color: '#f07d1e' },
    politie: { icon: '🚔', color: '#2563eb' },
    lifeliner: { icon: '🚁', color: '#7c4dcc' },
    knrm: { icon: '⛵', color: '#0f9d76' },
    overig: { icon: '📟', color: '#868d95' },
};

// Read a colour token from the CSS, so dark mode's lifted colours reach the
// map too. The hexes above stay as the fallback. Re-read on every theme change
// (zl:themechange, from _theme_head.html) — see the listener below.
function cssToken(name, fallback) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}
const SERVICE_FALLBACKS = Object.fromEntries(Object.entries(SERVICES).map(([slug, s]) => [slug, s.color]));
function readServiceColors() {
    for (const [slug, s] of Object.entries(SERVICES)) s.color = cssToken(`--${slug}`, SERVICE_FALLBACKS[slug]);
}
readServiceColors();

// Priority code mappings
export const PRIORITY_LABELS = {
    'A1': 'SPOED',
    'A2': 'URGENT',
    'B1': 'GEPLAND',
    'B2': 'GEPLAND',
    'P1': 'SPOED',
    'P2': 'PRIO 2',
};

// Distance visualization
export const DISTANCE_LINE = {
    minWidth: 30,
    maxWidth: 150,
    minDist: 0.3,
    maxDist: 100,
};

// Map padding for bounds fitting
// Left: 380px sidebar (360px + buffer) on desktop, Top: 70px navbar
// Bottom/Right: small buffer
export const MAP_PADDING = {
    paddingTopLeft: [380, 70],
    paddingBottomRight: [40, 40],
    maxZoom: 15,
};

// Firebase Cloud Messaging (web push)
export const FIREBASE_CONFIG = {
    apiKey: 'AIzaSyAXP57zjuSVTG-iTu8ck7by0tmLrKrp6xo',
    projectId: 'ramptoerist-49e07',
    messagingSenderId: '681493253688',
    appId: '1:681493253688:web:8761a1b3baa7e986b2a0cd',
};

export const VAPID_KEY = 'BKZvwNkU1jF16E8l-KP8lHpXe_-vFOBfvDWmt_GVZOeC7-Qy8lVLdK3Be843MuNPJbwt7uXzgVdz1gFJ8JeUzbc';

// Burst accent color (zwaailicht logo blue) — used for the map burst circle
// stroke/fill. One accent for every active piek (severity isn't surfaced).
// Keep in sync with --burst-accent in shared.css. The OG/social cards use the
// dominant-service colour instead, so there's no third value to sync.
export let BURST_ACCENT_COLOR = cssToken('--burst-accent', '#0b6e99');

// Registered at module load, so it runs before map.js repaints on the same event.
document.addEventListener('zl:themechange', () => {
    readServiceColors();
    BURST_ACCENT_COLOR = cssToken('--burst-accent', '#0b6e99');
});

// Toast when push is blocked. In the Android app the block is the app's
// notification setting, which the browser's site settings do not reach.
export const PUSH_BLOCKED_MESSAGE = 'Notificaties geblokkeerd — zet ze aan bij Instellingen › Apps › Zwaailicht (of in je browser)';

// Local storage keys
export const STORAGE_KEYS = {
    manualLocation: 'manualLocation',
    // '1' once the reader shared their GPS position (map, front page or the
    // push panel): the next visit locates again without the button. Only the
    // fact is kept, never the position. Needed because
    // permissions.query('geolocation') doesn't reliably report 'granted'
    // (one-time grants; the Android app), which left the button on every visit.
    geoShared: 'geoShared',
    webPushToken: 'webPushToken',
    webPushRadius: 'webPushRadius',
    webPushServices: 'webPushServices',
    webPushCities: 'webPushCities',
    webPushPrefs: 'webPushPrefs',
    webPushBursts: 'webPushBursts',
    pushLocationSent: 'pushLocationSent',
    filterRadius: 'filterRadius',
};
